import { Avatar, AvatarGroup, Link as MuiLink } from '@mui/material'
import { type GridColDef } from '@mui/x-data-grid-pro'
import {
    type GetRosterSearchV2Query,
    SortIndexField,
} from '__generated__/graphql'
import { formatDate } from 'date-fns'
import { Link } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'
import RosterTypePill from 'shared/components/RosterTypePill'
import { formatCompactNumber, getFullName, getInitials } from 'shared/utils'
import { type RosterSortField } from 'shared/hooks/rosterSqlSearchUrlState'
import RosterStatusPill from './RosterStatusPill'

export type RosterTableRowModel =
    GetRosterSearchV2Query['rosterSearchV2']['data'][number]

export type RosterTableColumnDefinition = GridColDef<RosterTableRowModel> & {
    sortIndexField?: SortIndexField
    sortField?: RosterSortField
}

const smallAvatarSx = { height: '1.5rem', width: '1.5rem', fontSize: '0.75rem' }

export const rosterTableColumns: RosterTableColumnDefinition[] = [
    {
        field: 'rosterName',
        headerName: 'Search Name',
        renderCell: ({ row }) => (
            <MuiLink
                component={Link}
                to={`${recruiterRouteConfig.rosters.path}/${row.id}`}
            >
                {row.name}
            </MuiLink>
        ),
        width: 200,
        sortable: true,
        sortIndexField: SortIndexField.RosterName,
        sortField: 'name',
    },
    {
        field: 'candidateCount',
        headerName: 'Count',
        valueGetter: (_, { candidateCount }) =>
            formatCompactNumber(candidateCount),
        sortable: true,
        sortIndexField: SortIndexField.RosterCandidateCount,
        sortField: 'candidateCount',
        width: 70,
        align: 'center',
    },
    {
        field: 'company',
        headerName: 'Company',
        renderCell: ({ row }) =>
            row.company ? (
                <MuiLink
                    component={Link}
                    to={`${recruiterRouteConfig.companies.path}/${row.company.id}`}
                >
                    {row.company.name}
                </MuiLink>
            ) : (
                '-'
            ),
        sortable: true,
        sortIndexField: SortIndexField.RosterCompany,
        sortField: 'company',
        width: 200,
    },
    {
        field: 'fundSize',
        headerName: 'Fund Size',
        renderCell: ({ row }) => {
            const fundSizeString =
                row.fundSize
                    ?.split(';')
                    .map((s) => s.trim())
                    .join(', ') || '-'
            return <span title={fundSizeString}>{fundSizeString}</span>
        },
        sortable: true,
        sortField: 'fundSize',
        width: 200,
    },
    {
        field: 'consultants',
        headerName: 'Consultants',
        valueGetter: (_, row) => (row.consultants ? 'Yes' : 'No'),
        sortable: true,
        sortField: 'consultants',
        width: 100,
    },
    {
        field: 'visaStatuses',
        headerName: 'Visa',
        renderCell: ({ row }) => {
            const str =
                row.visaStatuses
                    ?.split(';')
                    .map((s) => s.trim())
                    .join(', ') || '-'
            return <span title={str}>{str}</span>
        },
        sortable: false,
        width: 200,
    },
    {
        field: 'createdAt',
        headerName: 'Creation Date',
        valueGetter: (_, { createdAt }) =>
            formatDate(new Date(createdAt), 'MM/dd/yyyy'),
        sortable: true,
        sortIndexField: SortIndexField.RosterCreatedAt,
        sortField: 'createdAt',
        width: 150,
    },
    {
        field: 'createdBy',
        headerName: 'Created By',
        valueGetter: (_, { createdBy }) =>
            createdBy ? getFullName(createdBy) : '-',
        sortable: true,
        sortIndexField: SortIndexField.RosterCreatedBy,
        sortField: 'createdBy',
        width: 150,
    },
    {
        field: 'internalUsers',
        headerName: 'Internal Users',
        renderCell: ({ row }) => (
            <AvatarGroup
                max={4}
                sx={{
                    alignItems: 'center',
                    height: '100%',
                    justifyContent: 'flex-end',
                }}
                slotProps={{ additionalAvatar: { sx: smallAvatarSx } }}
            >
                {row.sharedRecruiters?.map((recruiter) => (
                    <Avatar
                        key={recruiter.id}
                        sx={smallAvatarSx}
                        title={getFullName(recruiter)}
                    >
                        {getInitials(recruiter)}
                    </Avatar>
                ))}
            </AvatarGroup>
        ),
        sortable: false,
        width: 150,
    },
    {
        field: 'status',
        headerName: 'Status',
        renderCell: ({ row }) => <RosterStatusPill status={row.status ?? ''} />,
        sortable: true,
        sortIndexField: SortIndexField.RosterStatus,
        sortField: 'status',
        width: 150,
    },
    {
        field: 'rosterType',
        headerName: 'Search Type',
        renderCell: ({ row }) => <RosterTypePill rosterType={row.rosterType} />,
        sortable: true,
        sortIndexField: SortIndexField.RosterType,
        sortField: 'type',
        width: 150,
    },
]
